import { useState } from 'react'
import { Table, useApiQuery, useToast, type Column } from 'grabbitbot-legacy-ui'
import { wnfClient } from '../../api'
import type { LimitOrder } from '../../api/types'
import { PageState } from '../../components/PageState'
import { TestFundsBanner } from '../../components/TestFundsBanner'
import { describeLoadError } from '../../lib/errors'
import { numericSortKey, utcDateTime } from '../../lib/format'

/** Open LIMIT orders (backend batch W5) — placed by the bot through the API;
 * this screen lists and cancels them. There is no manual order entry (the
 * legacy UI had none either — audit §12). */
export function OpenOrdersPage() {
  const { data, loading, error, refetch } = useApiQuery(() => wnfClient.searchOpenOrders(), [])
  const { showToast } = useToast()
  const [confirming, setConfirming] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  async function cancel(reference: string) {
    setBusy(reference)
    try {
      await wnfClient.cancelOpenOrder(reference)
      showToast(`Order ${reference} cancelled — reserved test funds released`, 'success')
      refetch()
    } catch {
      showToast('Could not cancel the order (it may already be filled or cancelled).', 'error')
      refetch()
    } finally {
      setBusy(null)
      setConfirming(null)
    }
  }

  const columns: Column<LimitOrder>[] = [
    { key: 'coinPair', label: 'Pair', align: 'left' },
    {
      key: 'side',
      label: 'Side',
      align: 'left',
      format: (v) => <span className={`wnf-badge wnf-badge--${v === 'buy' ? 'buy' : 'sell'}`}>{String(v).toUpperCase()}</span>,
    },
    { key: 'quantity', label: 'Quantity', numeric: true, cellClass: () => 'wnf-num', sortValue: (r) => numericSortKey(r.quantity) },
    { key: 'price', label: 'Limit price', numeric: true, cellClass: () => 'wnf-num', sortValue: (r) => numericSortKey(r.price) },
    {
      key: 'reserved',
      label: 'Reserved',
      numeric: true,
      cellClass: () => 'wnf-num',
      format: (_v, r) => `${r.reserved} ${r.reservedCoin}`,
      sortValue: (r) => numericSortKey(r.reserved),
    },
    { key: 'createdAt', label: 'Placed', align: 'left', format: (v) => utcDateTime(v as string) },
    { key: 'reference', label: 'Reference', align: 'left' },
    {
      key: 'id',
      label: '',
      sortable: false,
      align: 'left',
      format: (_v, r) =>
        confirming === r.reference ? (
          <span className="wnf-actions">
            <button type="button" disabled={busy === r.reference} onClick={() => void cancel(r.reference)}>
              Confirm cancel
            </button>
            <button type="button" onClick={() => setConfirming(null)}>
              Keep
            </button>
          </span>
        ) : (
          <button type="button" onClick={() => setConfirming(r.reference)} aria-label={`Cancel order ${r.reference}`}>
            Cancel
          </button>
        ),
    },
  ]

  return (
    <div>
      <div className="wnf-page-title">
        <h1>Open orders</h1>
        <button type="button" onClick={refetch}>
          Refresh
        </button>
      </div>
      <TestFundsBanner />
      <PageState loading={loading} error={error ? describeLoadError(error, 'open orders') : null}>
        {data && data.length === 0 && <p className="wnf-muted">No open orders.</p>}
        {data && data.length > 0 && (
          <Table columns={columns} rows={data} rowKey={(r) => r.id} initialSort={{ key: 'createdAt', dir: 'desc' }} />
        )}
      </PageState>
    </div>
  )
}
